import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  Note,
  SessionStatus,
  StartOptions,
  VOX_SESSION_COMPLETED,
  VoxClient,
  VoxSdk,
  truncateVoxContext,
} from '@principle-theorem/ai-scribe-sdk';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  SCRIBE_HEARTBEAT_INTERVAL_SECONDS,
  SCRIBE_VOX_CLOCK_SKEW_MS,
  ScribePatientContext,
  ScribeSession,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  IMintTokenRequest,
  IMintTokenResponse,
  IPatient,
  IPractice,
  IScribeSession,
  IStaffer,
  MicrophoneDenialReason,
  SCRIBE_ACTIVE_PHASES,
  SCRIBE_IN_PROGRESS_STATUSES,
  ScribeRecordingPhase,
  ScribeSessionSource,
  ScribeSessionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  ISODateType,
  Reffable,
  SerialisedData,
  WithRef,
  filterUndefined,
  httpsCallable,
  retryWithBackoff,
  serialise,
  snapshot,
  snapshotDefined,
  toTimestamp,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { take } from 'rxjs/operators';
import { ScribeStatusResolver } from './scribe-status-resolver';

/**
 * An accepted note on its way to Firestore, held in memory while its
 * persistence is retried or after it failed, because the vendor cannot
 * re-deliver a note once it has been emitted.
 */
interface IBufferedNote {
  /**
   * `voxNoteId|content`: VoxDenta rewrites a note under the same id, so each
   * accepted version is a note of its own.
   */
  key: string;
  voxNoteId: string;
  content: string;
  name?: string;
}

/** Everything the lifecycle needs to reference the live session after start. */
interface IScribeSessionContext {
  /**
   * The signed-in staffer, NOT the "Recording As" staffer the session is
   * attributed to. The single-session lease and the vendor token are both keyed
   * per authenticated staffer, so honouring a Recording-As selection here would
   * let one clinician steal another's lease.
   */
  authenticatedStaffer: WithRef<IStaffer>;
  practiceRef: DocumentReference<IPractice>;
  sessionRef: DocumentReference<IScribeSession>;
}

/**
 * VoxDenta note statuses meaning no note is coming (28 Oct 2026): `empty` when
 * no audio reached VoxDenta, `failed` when writing failed after retries.
 */
enum VoxNoteUnavailableStatus {
  Failed = 'failed',
  Empty = 'empty',
}

const SCRIBE_MINT_TOKEN_CALLABLE = 'http-aiScribe-mintToken';
const SCRIBE_ANALYZER_BAND_COUNT = 32;
const MILLISECONDS_PER_SECOND = 1000;
const ELAPSED_TICK_INTERVAL_MS = 1000;
const BAND_LEVEL_POLL_INTERVAL_MS = 100;
const TOKEN_REFRESH_LEAD_MS = 60_000;
// VoxDenta, 28 Oct 2026: wait up to 10 minutes after stop(); median 63 s, p90
// 2 min, max seen 4 m 50 s; capacity retries add minutes.
const WRITING_TERMINAL_TIMEOUT_MS = 600_000;
// VoxDenta, 28 Oct 2026: p90 from stop to note is 2 minutes.
const FINISH_IN_BACKGROUND_AFTER_SECONDS = 120;
// SDK 0.0.9 derives `completed` client-side as "every note is `ready`" and
// emits it only when the notes change, so it says nothing about the stop-time
// rule; a settled batch without a post-stop `completed` ends the wait only if
// no further batch arrives within 15 s.
const SETTLED_BATCH_GRACE_MS = 15_000;
const NOTE_PERSIST_MAX_ATTEMPTS = 3;
// VoxDenta, 28 Oct 2026. `empty` and `writing` (a transcription failure stays
// `writing`) are API values absent from the 0.0.9 SDK type, so note statuses
// are compared as strings; anything other than `ready` or unavailable is still
// being written.
const VOX_NOTE_READY = 'ready';
const VOX_NOTE_UNAVAILABLE_STATUSES: readonly string[] = Object.values(
  VoxNoteUnavailableStatus
);
const SCRIBE_ERROR_MESSAGE = 'AI Scribe encountered an error.';
const SCRIBE_CONCURRENT_SESSION_MESSAGE =
  'Another AI Scribe session is already active for you. Finish or discard it before starting a new one.';
const SCRIBE_TOKEN_MINT_ERROR_MESSAGE =
  'Could not start AI Scribe. Please try again.';
const SCRIBE_TOKEN_REFRESH_ERROR_MESSAGE =
  'AI Scribe session expired and was stopped. Any completed notes have been saved.';
const SCRIBE_LEASE_LOST_MESSAGE =
  'AI Scribe was taken over on another device and stopped. Any completed notes have been saved.';
const SCRIBE_HEARTBEAT_FAILURE_MESSAGE =
  'AI Scribe lost its connection and was stopped. Any completed notes have been saved.';
const SCRIBE_NOTE_RECOVERY_MESSAGE = `AI Scribe is still writing this note. It will appear in this patient's scribe notes when it's ready.`;
const SCRIBE_NO_NOTE_EMPTY_MESSAGE = `AI Scribe didn't receive any audio for this recording. Please check your microphone and dictate your notes.`;
const SCRIBE_NO_NOTE_FAILED_MESSAGE = `AI Scribe couldn't write a note for this recording. Please dictate your notes.`;
const SCRIBE_MAX_CONSECUTIVE_HEARTBEAT_FAILURES = 3;

/**
 * Owns the full live AI Scribe session lifecycle for the current staffer: the
 * single-session lease, VoxDenta SDK wiring, heartbeat, note persistence buffer,
 * mid-session token refresh, the post-stop writing window and the unload guard.
 * It follows VoxDenta's rules (28 Oct 2026): only a `ready` note last written
 * after stop is final, the wait lasts up to 10 minutes, and a wait that ends
 * without the note hands the session to the recovery sweep and leaves the
 * client open until the next recording, so its last audio still uploads.
 * Root-provided so a single lease/heartbeat exists per staffer across the app.
 */
@Injectable({
  providedIn: 'root',
})
export class AiScribeSessionService {
  private readonly _org = inject(OrganisationService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _router = inject(Router);

  private readonly _activeSession = signal<
    Reffable<IScribeSession> | undefined
  >(undefined);
  private readonly _boundPatient = signal<WithRef<IPatient> | undefined>(
    undefined
  );
  private readonly _phase = signal<ScribeRecordingPhase>(
    ScribeRecordingPhase.Idle
  );
  private readonly _elapsed = signal(0);
  private readonly _writingElapsed = signal(0);
  /** Mirrors a pending `_writingResolve`, so `canFinishInBackground` reacts. */
  private readonly _awaitingNote = signal(false);
  private readonly _bandLevels = signal<Float32Array>(new Float32Array(0));
  private readonly _persistenceError = signal(false);
  private readonly _failureMessage = signal<string | undefined>(undefined);
  private readonly _noteRecoveryPending = signal(false);
  private readonly _microphoneDenialReason = signal<
    MicrophoneDenialReason | undefined
  >(undefined);
  private readonly _launchUrl = signal<string | undefined>(undefined);

  private _vox?: VoxClient;
  /**
   * A client whose session finalised after stop but before its note arrived or
   * the 10-minute limit passed, left open for its last audio to upload. Only
   * `_closeDetachedVox` clears it: dropping it anywhere else, including
   * `_resetSessionState`, would leave that client open for good.
   */
  private _detachedVox?: VoxClient;
  private _context?: IScribeSessionContext;
  private _currentToken = '';
  private _currentTokenExpiresAt = 0;
  private _voxUnsubscribers: (() => void)[] = [];
  private _lastRequest?: {
    patient: WithRef<IPatient>;
    staffer: WithRef<IStaffer>;
    recordDate: ISODateType;
    appointment?: WithRef<IAppointment>;
  };

  private readonly _acceptedNoteKeys = new Set<string>();
  private readonly _persistedNoteKeys = new Set<string>();
  private readonly _bufferedNotes = new Map<string, IBufferedNote>();
  private _persistTasks: Promise<void>[] = [];

  private _stoppedAtMs?: number;
  private _postStopCompleted = false;
  private _latestBatchSettled = false;
  private _unavailableReason?: VoxNoteUnavailableStatus;
  private _recoveryRequested = false;
  private _sdkErrored = false;
  private _terminalReached = false;
  private _writingTimedOut = false;
  private _tokenExpired = false;
  private _finalising = false;
  private _writingResolve?: () => void;
  private _consecutiveHeartbeatFailures = 0;

  private _heartbeatTimer?: ReturnType<typeof setInterval>;
  private _elapsedTimer?: ReturnType<typeof setInterval>;
  private _writingElapsedTimer?: ReturnType<typeof setInterval>;
  private _bandLevelTimer?: ReturnType<typeof setInterval>;
  private _tokenRefreshTimer?: ReturnType<typeof setTimeout>;
  private _tokenExpiryTimer?: ReturnType<typeof setTimeout>;
  private _writingTimeoutTimer?: ReturnType<typeof setTimeout>;
  private _settledGraceTimer?: ReturnType<typeof setTimeout>;

  private _beforeUnloadRegistered = false;

  readonly activeSession = this._activeSession.asReadonly();
  /**
   * The patient the live session is recording for, so a consumer can show the
   * name and gate the Insert affordance by patient ref.
   */
  readonly boundPatient = this._boundPatient.asReadonly();
  /**
   * The status a consumer renders, derived from the session + capture phase.
   * The microphone handshake precedes any session doc, so those phases are
   * resolved before the persisted status.
   */
  readonly status = computed(() =>
    ScribeStatusResolver.rendered(this._phase(), this._activeSession()?.status)
  );
  /**
   * True from the moment a start is requested until the session finalises —
   * covering the microphone handshake, the connecting window, live recording and
   * the post-stop writing window, but NOT the terminal `ready`/`failed` states a
   * finished session lingers in for review. A consumer gates starting a second
   * session on this, since `startSession` silently refuses while one is live.
   */
  readonly sessionInProgress = computed(() =>
    SCRIBE_IN_PROGRESS_STATUSES.includes(this.status())
  );
  readonly elapsed = this._elapsed.asReadonly();
  readonly bandLevels = this._bandLevels.asReadonly();
  readonly persistenceError = this._persistenceError.asReadonly();
  /**
   * Seconds since `stop` while the session waits for the vendor's final notes,
   * so a consumer can show the wait is progressing.
   */
  readonly writingElapsed = this._writingElapsed.asReadonly();
  /**
   * True from 2 minutes into the post-stop wait (VoxDenta's p90), so a
   * consumer can tell the clinician the note is still being written.
   */
  readonly stillWriting = computed(
    () =>
      this._phase() === ScribeRecordingPhase.Writing &&
      this._writingElapsed() >= FINISH_IN_BACKGROUND_AFTER_SECONDS
  );
  /**
   * Whether `finishInBackground` may end the wait: from 2 minutes in, while the
   * wait is pending, including after a note was accepted, since a second note
   * VoxDenta is still writing can hold the wait for up to 10 minutes.
   */
  readonly canFinishInBackground = computed(
    () => this.stillWriting() && this._awaitingNote()
  );
  /**
   * A user-facing reason for the most recent failure when one is known (the
   * no-note outcomes, and a note handed to the recovery sweep), so a consumer
   * can show it on the failed card; `undefined` for generic failures.
   */
  readonly failureMessage = this._failureMessage.asReadonly();
  /**
   * True once the session was handed to the recovery sweep, so a consumer can
   * say the note is still coming rather than that the session failed.
   */
  readonly noteRecoveryPending = this._noteRecoveryPending.asReadonly();
  /**
   * Why microphone access failed while `status` is `permissionDenied`, so a
   * consumer can distinguish a blocked prompt from a missing device.
   */
  readonly microphoneDenialReason = this._microphoneDenialReason.asReadonly();
  /**
   * The application route the session was launched from, captured at start, so
   * a consumer can return the user to where recording began once it finishes.
   */
  readonly launchUrl = this._launchUrl.asReadonly();

  constructor() {
    // Fail any prior live session orphaned by a crash/hard-reload before this
    // staffer starts a new one.
    this._org.staffer$
      .pipe(filterUndefined(), take(1), takeUntilDestroyed())
      .subscribe((staffer) => {
        void ScribeSession.sweepStale(staffer);
      });
  }

  /**
   * Start a live scribe session for `patient`, attributed to `staffer` on
   * `recordDate` — the "Recording As" clinician and charted day the caller is
   * working under, which need not be the signed-in staffer or today. Creates the
   * session doc, claims the SIGNED-IN staffer's single-session lease against it,
   * mints a VoxDenta token and begins recording — refusing (and failing the
   * just-created session) when a fresh foreign lease already exists, so
   * recording never starts twice.
   */
  async startSession(
    patient: WithRef<IPatient>,
    staffer: WithRef<IStaffer>,
    recordDate: ISODateType,
    appointment?: WithRef<IAppointment>
  ): Promise<void> {
    if (SCRIBE_ACTIVE_PHASES.includes(this._phase())) {
      return;
    }
    this._resetSessionState();
    this._launchUrl.set(this._router.url);
    this._lastRequest = { patient, staffer, recordDate, appointment };
    this._boundPatient.set(patient);

    // Microphone access is obtained BEFORE any session doc exists, so a denied prompt
    // never leaves an orphaned in-progress session behind.
    this._phase.set(ScribeRecordingPhase.RequestingPermission);
    const denialReason = await this._requestMicrophoneAccess();
    if (denialReason) {
      this._microphoneDenialReason.set(denialReason);
      this._phase.set(ScribeRecordingPhase.PermissionDenied);
      return;
    }
    // Hold `connecting` across doc creation + lease + token mint (10s+): dropping
    // to `idle` here lets `status` resolve to `in-progress` ("Rec") the instant
    // the session doc is set, before `_beginRecording` actually captures audio.
    this._phase.set(ScribeRecordingPhase.Connecting);

    const context = await this._createAndLeaseSession(
      patient,
      staffer,
      recordDate,
      appointment
    );
    if (!context) {
      return;
    }

    const minted = await this._tryMint(context);
    if (!minted) {
      await ScribeSession.releaseLease(context.authenticatedStaffer);
      await ScribeSession.markFailed({ ref: context.sessionRef });
      this._setLocalStatus(ScribeSessionStatus.Failed);
      this._phase.set(ScribeRecordingPhase.Idle);
      this._snackBar.open(SCRIBE_TOKEN_MINT_ERROR_MESSAGE);
      return;
    }

    // A rejected lazy SDK import or init/start would otherwise leak the open
    // microphone,
    // listeners, unload handler and held lease; `_finaliseFailed` runs the full
    // cleanup and lease release used by every other failure path.
    try {
      await this._beginRecording(context, minted);
    } catch {
      this._snackBar.open(SCRIBE_ERROR_MESSAGE);
      await this._finaliseFailed(context);
    }
  }

  /**
   * Re-attempt the most recent `startSession` after a recoverable refusal —
   * chiefly a `permission-denied` prompt the user has since allowed. A no-op
   * until something has been started.
   */
  async retrySession(): Promise<void> {
    const request = this._lastRequest;
    if (!request) {
      return;
    }
    await this.startSession(
      request.patient,
      request.staffer,
      request.recordDate,
      request.appointment
    );
  }

  /**
   * Stop recording and finalise. VoxDenta writes the note from the full
   * recording after `stop`, which can take minutes, so this waits up to 10
   * minutes for notes that are `ready` and were last written after stop — an
   * earlier `completed` or note is an interim one and is ignored — persisting
   * each as it arrives. The wait ends once the latest batch is settled and
   * VoxDenta reports `completed` or sends nothing new for 15 s; the session is
   * then marked ready, or failed if a note remains unpersisted. When every
   * note is `failed` or `empty` the session fails with that reason. A wait
   * that ends without the note (the 10-minute limit, an expired token,
   * `finishInBackground`) hands the session to the recovery sweep, which adds
   * the note once VoxDenta has it; the session fails unless a note was
   * already saved. Never finalises immediately after `stop`.
   */
  async stopSession(): Promise<void> {
    const context = this._context;
    if (!context || this._phase() !== ScribeRecordingPhase.Recording) {
      return;
    }
    this._phase.set(ScribeRecordingPhase.Writing);
    this._stopCaptureTimers();
    this._writingElapsedTimer = setInterval(() => {
      this._writingElapsed.update((seconds) => seconds + 1);
    }, ELAPSED_TICK_INTERVAL_MS);
    this._recordStop(context);
    this._vox?.stop();

    await this._awaitTerminalStatus();
    this._stopLivenessTimers();
    await this._settlePersistTasks();

    // A lease loss, repeated heartbeat failure or discard during the wait
    // already owns finalisation.
    if (this._context !== context || this._finalising) {
      return;
    }

    if (this._sdkErrored) {
      await this._finaliseFailed(context);
      return;
    }

    await this._flushBuffer();
    if (this._acceptedNoteKeys.size === 0) {
      const message = this._noNoteMessage();
      this._failureMessage.set(message);
      this._snackBar.open(message);
      await this._finaliseFailed(context);
      return;
    }
    if (this._bufferedNotes.size === 0 && this._allAcceptedPersisted()) {
      await this._finaliseReady(context);
    } else {
      await this._finaliseFailed(context);
    }
  }

  /**
   * Stops waiting for the note once `canFinishInBackground` allows it, so the
   * clinician can move on. With a note already accepted, the wait just ends
   * and `stopSession` readies the session with the notes it has, handing any
   * note still being written to the recovery sweep. Otherwise the session
   * fails with the recovery message, shown on the failed card rather than a
   * snackbar, and the recovery sweep adds the note once VoxDenta has it.
   * Either way the VoxDenta client is left open rather than closed, so its
   * last audio still uploads, until the next recording starts.
   */
  async finishInBackground(): Promise<void> {
    const context = this._context;
    if (!context || !this.canFinishInBackground()) {
      return;
    }
    if (this._acceptedNoteKeys.size > 0) {
      this._resolveWriting();
      return;
    }
    if (!this._claimFinalisation()) {
      return;
    }
    this._clearAllTimers();
    this._failureMessage.set(SCRIBE_NOTE_RECOVERY_MESSAGE);
    await this._failAndFinalise(context);
  }

  /**
   * User-initiated abandon: stop capture, fail the session and release the
   * lease immediately without waiting for the writing window.
   */
  async discardSession(): Promise<void> {
    const context = this._context;
    if (!context || !this._claimFinalisation()) {
      return;
    }
    this._vox?.stop();
    await this._settlePersistTasks();
    await ScribeSession.markFailed({ ref: context.sessionRef });
    this._setLocalStatus(ScribeSessionStatus.Failed);
    await this._finalise(context);
  }

  /**
   * VoxDenta (28 Oct 2026) emits an interim note mid-recording for recordings
   * over ~42 minutes and rewrites it from the full recording after stop, so
   * notes count only after stop. A `ready` note rejected as interim keeps the
   * batch unsettled, because its rewrite is still coming.
   */
  private readonly _handleNotes = (notes: Note[]): void => {
    if (this._finalising) {
      return;
    }
    const stoppedAtMs = this._stoppedAtMs;
    if (stoppedAtMs === undefined) {
      return;
    }
    this._clearSettledGraceTimer();
    this._latestBatchSettled =
      notes.length > 0 &&
      notes.every(
        (note) =>
          this._isAcceptable(note, stoppedAtMs) ||
          VOX_NOTE_UNAVAILABLE_STATUSES.includes(note.status)
      );
    for (const note of notes) {
      if (this._isAcceptable(note, stoppedAtMs)) {
        this._enqueueNotePersist(note);
      }
    }
    this._endWaitIfUnavailable(notes);
    this._maybeFinishWriting();
  };

  /**
   * SDK 0.0.9 derives `completed` client-side as "every note is `ready`", so a
   * `completed` before stop belongs to a long recording's interim note, and any
   * later status supersedes an earlier `completed`.
   */
  private readonly _handleStatus = (status: SessionStatus): void => {
    if (this._finalising) {
      return;
    }
    if (this._stoppedAtMs === undefined) {
      return;
    }
    if (status !== VOX_SESSION_COMPLETED) {
      this._postStopCompleted = false;
      return;
    }
    this._postStopCompleted = true;
    this._maybeFinishWriting();
  };

  private readonly _handleError = (): void => {
    this._snackBar.open(SCRIBE_ERROR_MESSAGE);
    this._sdkErrored = true;
    if (this._phase() === ScribeRecordingPhase.Writing) {
      this._resolveWriting();
      return;
    }
    if (this._phase() === ScribeRecordingPhase.Recording) {
      void this._finaliseFromError();
    }
  };

  private readonly _beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
    event.returnValue = '';
  };

  /**
   * Resolves org context, creates the session doc, stores its VoxDenta session
   * path (the recovery sweep needs it to fetch a note this client never
   * received) and claims the SIGNED-IN staffer's single-session lease. Returns
   * the live context on success, or `undefined` when refused (a fresh foreign
   * lease) or an await rejected (offline, Firestore unavailable).
   *
   * A rejection must never strand `_phase` at `connecting` —
   * `SCRIBE_ACTIVE_PHASES` treats that as live and blocks every future start — so
   * the catch resets `_phase` to `idle`, snackbars, and marks the doc failed when
   * it was already created. No lease release is needed: `acquireLease` is the
   * last await and commits atomically, so a throw means it was never claimed.
   */
  private async _createAndLeaseSession(
    patient: WithRef<IPatient>,
    staffer: WithRef<IStaffer>,
    recordDate: ISODateType,
    appointment?: WithRef<IAppointment>
  ): Promise<IScribeSessionContext | undefined> {
    let createdSessionRef: DocumentReference<IScribeSession> | undefined;
    try {
      const authenticatedStaffer = await snapshotDefined(this._org.staffer$);
      const practice = await snapshotDefined(this._org.practice$);
      const brand = await snapshotDefined(this._org.brand$);

      const session = ScribeSession.init({
        stafferRef: staffer.ref,
        practiceRef: practice.ref,
        brandRef: brand.ref,
        source: ScribeSessionSource.Live,
        recordDate,
        ...(appointment ? { appointmentRef: appointment.ref } : {}),
      });
      const sessionRef = await ScribeSession.create(patient, session);
      createdSessionRef = sessionRef;
      this._activeSession.set({ ...session, ref: sessionRef });
      await ScribeSession.setVoxSessionPath(
        { ref: sessionRef },
        ScribeSession.toVoxSessionPath(
          authenticatedStaffer.ref.id,
          sessionRef.id
        )
      );

      const { acquired } = await ScribeSession.acquireLease(
        authenticatedStaffer,
        sessionRef
      );
      if (!acquired) {
        await ScribeSession.markFailed({ ref: sessionRef });
        this._setLocalStatus(ScribeSessionStatus.Failed);
        this._phase.set(ScribeRecordingPhase.Idle);
        this._snackBar.open(SCRIBE_CONCURRENT_SESSION_MESSAGE);
        return;
      }

      return {
        authenticatedStaffer,
        practiceRef: practice.ref,
        sessionRef,
      };
    } catch {
      this._snackBar.open(SCRIBE_ERROR_MESSAGE);
      if (createdSessionRef) {
        await ScribeSession.markFailed({ ref: createdSessionRef });
        this._setLocalStatus(ScribeSessionStatus.Failed);
      }
      this._phase.set(ScribeRecordingPhase.Idle);
      return;
    }
  }

  private async _requestMicrophoneAccess(): Promise<
    MicrophoneDenialReason | undefined
  > {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Only the permission grant is needed here; the SDK opens its own stream
      // on `start`, so the probe tracks are released immediately.
      for (const track of stream.getTracks()) {
        track.stop();
      }
      return;
    } catch (error) {
      return ScribeStatusResolver.microphoneDenialReason(error);
    }
  }

  private async _beginRecording(
    context: IScribeSessionContext,
    minted: IMintTokenResponse
  ): Promise<void> {
    this._closeDetachedVox();
    const factory = VoxSdk.getFactory();
    const sdk = await factory();
    this._currentToken = minted.token;
    this._currentTokenExpiresAt = minted.expiresAt;
    const vox = sdk.initializeVox(() => this._currentToken);
    this._vox = vox;
    this._context = context;
    this._voxUnsubscribers = [
      vox.on('notes', this._handleNotes),
      vox.on('status', this._handleStatus),
      vox.on('error', this._handleError),
    ];
    this._registerBeforeUnload();
    // A live session always has a bound patient; the guard keeps the type honest
    // and avoids sending an empty context if that ever stops holding.
    const patient = this._boundPatient();
    // No start instant is sent: since @voxhq/web-sdk 0.0.9 the live `start()`
    // takes none, so VoxDenta stamps the note with the recording instant.
    vox.start(
      ScribeSession.toVoxSessionPath(
        context.authenticatedStaffer.ref.id,
        context.sessionRef.id
      ),
      this._buildStartOptions(patient)
    );
    this._phase.set(ScribeRecordingPhase.Recording);
    this._startCaptureTimers(vox);
    this._startHeartbeat(context);
    this._scheduleTokenRefresh(minted.expiresAt);
  }

  /**
   * The per-session options passed to `start()`, carrying the patient context
   * clamped to the length VoxDenta accepts. Returns `undefined` without a
   * bound patient, so no empty context is sent.
   */
  private _buildStartOptions(
    patient: WithRef<IPatient> | undefined
  ): StartOptions | undefined {
    if (!patient) {
      return;
    }
    return { context: truncateVoxContext(ScribePatientContext.build(patient)) };
  }

  /**
   * VoxDenta's rule (28 Oct 2026): a note is final once it is `ready` and was
   * last written after stop. The allowance absorbs a clinic PC clock running
   * fast against VoxDenta's.
   */
  private _isAcceptable(note: Note, stoppedAtMs: number): boolean {
    return (
      note.status === VOX_NOTE_READY &&
      note.content.trim().length > 0 &&
      moment(note.updatedAt).valueOf() >= stoppedAtMs - SCRIBE_VOX_CLOCK_SKEW_MS
    );
  }

  private _enqueueNotePersist(note: Note): void {
    const accepted: IBufferedNote = {
      key: `${note.id}|${note.content}`,
      voxNoteId: note.id,
      content: note.content,
      name: note.name,
    };
    if (this._acceptedNoteKeys.has(accepted.key)) {
      return;
    }
    this._acceptedNoteKeys.add(accepted.key);
    const task = this._persistNote(accepted).finally(() => {
      this._persistTasks = this._persistTasks.filter(
        (pending) => pending !== task
      );
    });
    this._persistTasks.push(task);
  }

  private async _persistNote(note: IBufferedNote): Promise<void> {
    const context = this._context;
    if (!context) {
      return;
    }
    try {
      await retryWithBackoff(
        () => this._writeNote(context.sessionRef, note),
        () => true,
        NOTE_PERSIST_MAX_ATTEMPTS
      );
      this._persistedNoteKeys.add(note.key);
      this._bufferedNotes.delete(note.key);
      if (this._bufferedNotes.size === 0) {
        this._persistenceError.set(false);
      }
    } catch {
      this._bufferedNotes.set(note.key, note);
      this._persistenceError.set(true);
    }
  }

  private async _writeNote(
    sessionRef: DocumentReference<IScribeSession>,
    note: IBufferedNote
  ): Promise<void> {
    await ScribeSession.addNote(
      { ref: sessionRef },
      { content: note.content, voxNoteId: note.voxNoteId, name: note.name }
    );
  }

  private async _flushBuffer(): Promise<void> {
    const bufferedNotes = [...this._bufferedNotes.values()];
    for (const note of bufferedNotes) {
      const flushed = await this._tryFlushNote(note);
      if (flushed) {
        this._bufferedNotes.delete(note.key);
        this._persistedNoteKeys.add(note.key);
      }
    }
    this._persistenceError.set(this._bufferedNotes.size > 0);
  }

  private async _tryFlushNote(note: IBufferedNote): Promise<boolean> {
    const context = this._context;
    if (!context) {
      return false;
    }
    try {
      await this._writeNote(context.sessionRef, note);
      return true;
    } catch {
      return false;
    }
  }

  private _allAcceptedPersisted(): boolean {
    return this._persistedNoteKeys.size === this._acceptedNoteKeys.size;
  }

  /**
   * Unless VoxDenta reported every note `failed` or `empty`, a wait that ended
   * with no accepted note hit the 10-minute limit or an expired token, and the
   * recovery sweep will add the note once VoxDenta has it.
   */
  private _noNoteMessage(): string {
    switch (this._unavailableReason) {
      case VoxNoteUnavailableStatus.Failed:
        return SCRIBE_NO_NOTE_FAILED_MESSAGE;
      case VoxNoteUnavailableStatus.Empty:
        return SCRIBE_NO_NOTE_EMPTY_MESSAGE;
      default:
        return SCRIBE_NOTE_RECOVERY_MESSAGE;
    }
  }

  /**
   * Forgets any `completed` or batch from before stop and stamps the instant a
   * note must postdate, persisting it for the recovery sweep.
   */
  private _recordStop(context: IScribeSessionContext): void {
    this._stoppedAtMs = moment().valueOf();
    this._terminalReached = false;
    this._postStopCompleted = false;
    this._latestBatchSettled = false;
    void this._tryMarkStopped(context, this._stoppedAtMs);
  }

  private async _tryMarkStopped(
    context: IScribeSessionContext,
    stoppedAtMs: number
  ): Promise<void> {
    try {
      await ScribeSession.markStopped(
        { ref: context.sessionRef },
        toTimestamp(moment(stoppedAtMs))
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('AI Scribe: failed to record the stop instant', error);
    }
  }

  /**
   * VoxDenta (28 Oct 2026) reports `empty` when no audio reached it and
   * `failed` when writing failed after retries, and the SDK emits neither
   * `completed` nor an error for them, so a batch of only those ends the wait.
   */
  private _endWaitIfUnavailable(notes: Note[]): void {
    const allUnavailable =
      notes.length > 0 &&
      notes.every((note) =>
        VOX_NOTE_UNAVAILABLE_STATUSES.includes(note.status)
      );
    if (this._acceptedNoteKeys.size > 0 || !allUnavailable) {
      return;
    }
    this._unavailableReason = notes.some(
      (note) => note.status === VoxNoteUnavailableStatus.Failed
    )
      ? VoxNoteUnavailableStatus.Failed
      : VoxNoteUnavailableStatus.Empty;
    this._resolveWriting();
  }

  /**
   * Ends the wait once a note was accepted and the latest batch is settled: at
   * once on a post-stop `completed`, otherwise when no further batch arrives
   * within the settle grace. A `completed` with an unsettled batch, such as an
   * interim note still awaiting its rewrite, does not end it.
   */
  private _maybeFinishWriting(): void {
    if (this._acceptedNoteKeys.size === 0 || !this._latestBatchSettled) {
      return;
    }
    if (this._postStopCompleted) {
      this._finishWriting();
      return;
    }
    this._clearSettledGraceTimer();
    this._settledGraceTimer = setTimeout(() => {
      if (this._finalising) {
        return;
      }
      this._finishWriting();
    }, SETTLED_BATCH_GRACE_MS);
  }

  private _finishWriting(): void {
    this._terminalReached = true;
    this._resolveWriting();
  }

  private _clearSettledGraceTimer(): void {
    if (this._settledGraceTimer) {
      clearTimeout(this._settledGraceTimer);
      this._settledGraceTimer = undefined;
    }
  }

  private _awaitTerminalStatus(): Promise<void> {
    if (
      this._terminalReached ||
      this._sdkErrored ||
      this._tokenExpired ||
      this._unavailableReason !== undefined
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this._writingResolve = resolve;
      this._awaitingNote.set(true);
      this._writingTimeoutTimer = setTimeout(() => {
        this._writingTimedOut = true;
        this._resolveWriting();
      }, WRITING_TERMINAL_TIMEOUT_MS);
    });
  }

  private _resolveWriting(): void {
    if (this._writingTimeoutTimer) {
      clearTimeout(this._writingTimeoutTimer);
      this._writingTimeoutTimer = undefined;
    }
    this._clearSettledGraceTimer();
    const resolve = this._writingResolve;
    this._writingResolve = undefined;
    this._awaitingNote.set(false);
    resolve?.();
  }

  private async _settlePersistTasks(): Promise<void> {
    await Promise.allSettled([...this._persistTasks]);
  }

  private async _finaliseFromError(): Promise<void> {
    const context = this._context;
    if (!context) {
      return;
    }
    this._phase.set(ScribeRecordingPhase.Writing);
    this._stopCaptureTimers();
    await this._settlePersistTasks();
    await this._finaliseFailed(context);
  }

  private async _finaliseReady(context: IScribeSessionContext): Promise<void> {
    if (!this._claimFinalisation()) {
      return;
    }
    if (this._shouldRequestRecovery()) {
      await this._tryRequestRecovery(context);
    }
    const marked = await this._tryMarkReady(context);
    if (marked) {
      this._setLocalStatus(ScribeSessionStatus.Ready);
    } else {
      this._snackBar.open(SCRIBE_ERROR_MESSAGE);
      this._setLocalStatus(ScribeSessionStatus.Failed);
    }
    await this._finalise(context);
  }

  private async _tryMarkReady(
    context: IScribeSessionContext
  ): Promise<boolean> {
    try {
      await ScribeSession.markReady(
        { ref: context.sessionRef },
        this._elapsed()
      );
      return true;
    } catch {
      return false;
    }
  }

  private async _finaliseFailed(context: IScribeSessionContext): Promise<void> {
    if (!this._claimFinalisation()) {
      return;
    }
    await this._failAndFinalise(context);
  }

  /**
   * Claims the session's single finalisation; false when another path already
   * owns it. Once claimed, the SDK's notes and statuses are ignored: its
   * listeners stay subscribed until `_finalise`, and a late batch must not flip
   * `_terminalReached` and turn a detach into a close.
   */
  private _claimFinalisation(): boolean {
    if (this._finalising) {
      return false;
    }
    this._finalising = true;
    return true;
  }

  /**
   * Fails the session once the caller has claimed finalisation. When the note
   * may still come, the session is handed to the recovery sweep first and,
   * unless a reason is already showing, the clinician is told the note will
   * follow.
   */
  private async _failAndFinalise(
    context: IScribeSessionContext
  ): Promise<void> {
    if (this._shouldRequestRecovery()) {
      await this._tryRequestRecovery(context);
      if (!this._failureMessage()) {
        this._failureMessage.set(SCRIBE_NOTE_RECOVERY_MESSAGE);
      }
    }
    await ScribeSession.markFailed({ ref: context.sessionRef });
    if (this._bufferedNotes.size > 0) {
      this._persistenceError.set(true);
    }
    this._setLocalStatus(ScribeSessionStatus.Failed);
    await this._finalise(context);
  }

  /**
   * Whether VoxDenta may still hold a note this session has not saved, so the
   * recovery sweep must fetch it: after stop, unless VoxDenta reported every
   * note `failed` or `empty`, when no note was accepted, the latest batch was
   * still being written, or an accepted note could not be saved.
   */
  private _shouldRequestRecovery(): boolean {
    return (
      this._stoppedAtMs !== undefined &&
      this._unavailableReason === undefined &&
      (this._acceptedNoteKeys.size === 0 ||
        !this._latestBatchSettled ||
        this._bufferedNotes.size > 0)
    );
  }

  /**
   * Hands the session to the recovery sweep, at most once per session. Best
   * effort: a failed request must not block finalisation, though the note then
   * stays only with VoxDenta.
   */
  private async _tryRequestRecovery(
    context: IScribeSessionContext
  ): Promise<void> {
    if (this._recoveryRequested) {
      return;
    }
    this._recoveryRequested = true;
    this._noteRecoveryPending.set(true);
    try {
      await ScribeSession.requestNoteRecovery({ ref: context.sessionRef });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('AI Scribe: failed to request note recovery', error);
    }
  }

  private async _finalise(context: IScribeSessionContext): Promise<void> {
    this._clearAllTimers();
    this._resolveWriting();
    this._unsubscribeVox();
    this._closeOrDetachVox();
    this._removeBeforeUnload();
    this._phase.set(ScribeRecordingPhase.Idle);
    this._context = undefined;
    await ScribeSession.releaseLease(context.authenticatedStaffer);
  }

  /**
   * VoxDenta (28 Oct 2026): `close()` stops uploads at once, so closing a
   * client after stop but before its note arrived or the 10-minute limit
   * passed strands the last audio part and the end-of-recording signal. That
   * client is detached instead, and closed when the next recording starts.
   */
  private _closeOrDetachVox(): void {
    const awaitingNote =
      this._stoppedAtMs !== undefined &&
      !this._terminalReached &&
      !this._writingTimedOut &&
      this._unavailableReason === undefined;
    if (awaitingNote) {
      this._detachedVox = this._vox;
    } else {
      this._vox?.close();
    }
    this._vox = undefined;
  }

  private _closeDetachedVox(): void {
    this._detachedVox?.close();
    this._detachedVox = undefined;
  }

  private async _mintToken(
    context: IScribeSessionContext
  ): Promise<IMintTokenResponse> {
    const request: IMintTokenRequest = {
      practiceRef: context.practiceRef,
      stafferRef: context.authenticatedStaffer.ref,
      sessionId: context.sessionRef.id,
    };
    return snapshot(
      httpsCallable<SerialisedData<IMintTokenRequest>, IMintTokenResponse>(
        SCRIBE_MINT_TOKEN_CALLABLE
      )(serialise(request))
    );
  }

  private async _tryMint(
    context: IScribeSessionContext
  ): Promise<IMintTokenResponse | undefined> {
    try {
      return await this._mintToken(context);
    } catch {
      return;
    }
  }

  /**
   * Whether the lease (and the vendor token) must still be kept fresh. That
   * covers the whole post-stop wait as well as recording, because the lease goes
   * stale after `SCRIBE_LEASE_STALE_THRESHOLD_SECONDS` (120 s), well inside the
   * writing window. It ends the moment the wait resolves or finalisation is
   * claimed, so a finished session is never re-claimed.
   */
  private _isLeaseLive(): boolean {
    if (this._finalising) {
      return false;
    }
    const phase = this._phase();
    return (
      phase === ScribeRecordingPhase.Recording ||
      (phase === ScribeRecordingPhase.Writing && !!this._writingResolve)
    );
  }

  private _scheduleTokenRefresh(expiresAt: number): void {
    if (this._tokenRefreshTimer) {
      clearTimeout(this._tokenRefreshTimer);
      this._tokenRefreshTimer = undefined;
    }
    const delay = Math.max(
      0,
      expiresAt - moment().valueOf() - TOKEN_REFRESH_LEAD_MS
    );
    this._tokenRefreshTimer = setTimeout(() => {
      this._tokenRefreshTimer = undefined;
      void this._refreshToken();
    }, delay);
  }

  private async _refreshToken(): Promise<void> {
    const context = this._context;
    if (!context || !this._isLeaseLive()) {
      return;
    }
    const refreshed = await this._tryMint(context);
    if (this._context !== context || !this._isLeaseLive()) {
      return;
    }
    if (refreshed) {
      this._applyRefreshedToken(refreshed);
      return;
    }
    const retried = await this._tryMint(context);
    if (this._context !== context || !this._isLeaseLive()) {
      return;
    }
    if (retried) {
      this._applyRefreshedToken(retried);
      return;
    }
    this._scheduleTokenExpiry();
    if (this._phase() === ScribeRecordingPhase.Writing) {
      return;
    }
    this._snackBar.open(SCRIBE_TOKEN_REFRESH_ERROR_MESSAGE);
    await this.stopSession();
  }

  private _applyRefreshedToken(minted: IMintTokenResponse): void {
    this._currentToken = minted.token;
    this._currentTokenExpiresAt = minted.expiresAt;
    this._scheduleTokenRefresh(minted.expiresAt);
  }

  /**
   * Ends the writing wait once a token that could not be refreshed expires.
   * The SDK's notes poller retries a rejected token silently and never emits
   * `error`, so without this an expired session would sit out the full writing
   * timeout.
   */
  private _scheduleTokenExpiry(): void {
    const delay = Math.max(0, this._currentTokenExpiresAt - moment().valueOf());
    this._tokenExpiryTimer = setTimeout(() => {
      this._tokenExpiryTimer = undefined;
      this._tokenExpired = true;
      this._resolveWriting();
    }, delay);
  }

  private _startCaptureTimers(vox: VoxClient): void {
    this._elapsedTimer = setInterval(() => {
      this._elapsed.update((seconds) => seconds + 1);
    }, ELAPSED_TICK_INTERVAL_MS);
    this._bandLevelTimer = setInterval(() => {
      // The SDK's analyser allocates ONE Float32Array per band count and
      // mutates it in place on every call, so each poll returns the same
      // instance. A signal's default `Object.is` equality would treat that as
      // unchanged and never notify — copy into a fresh array so consumers see
      // every poll.
      this._bandLevels.set(
        new Float32Array(vox.getAnalyzerBandLevels(SCRIBE_ANALYZER_BAND_COUNT))
      );
    }, BAND_LEVEL_POLL_INTERVAL_MS);
  }

  private _startHeartbeat(context: IScribeSessionContext): void {
    this._heartbeatTimer = setInterval(() => {
      void this._writeHeartbeat(context);
    }, SCRIBE_HEARTBEAT_INTERVAL_SECONDS * MILLISECONDS_PER_SECOND);
  }

  private async _writeHeartbeat(context: IScribeSessionContext): Promise<void> {
    if (this._context !== context || !this._isLeaseLive()) {
      return;
    }
    try {
      await ScribeSession.touchHeartbeat({ ref: context.sessionRef });
      // The session may have finalised (and released its lease) while the
      // heartbeat write was in flight; re-claiming here would re-create a fresh
      // lease for a finished session and block the next start for the stale
      // window.
      if (this._context !== context || !this._isLeaseLive()) {
        return;
      }
      const { acquired } = await ScribeSession.acquireLease(
        context.authenticatedStaffer,
        context.sessionRef
      );
      if (this._context !== context || !this._isLeaseLive()) {
        // An acquire that was in flight while this session finalised has just
        // re-created a lease for a finished session, which would refuse this
        // staffer's next start for up to 120 s — release it again. `Idle` and
        // `PermissionDenied` are the only phases in which no newer start can
        // own the lease (`startSession` leaves `Idle` synchronously and a
        // denied microphone prompt never reaches the lease); `releaseLease` is
        // a hard delete, idempotent against `_finalise`'s own release.
        const phase = this._phase();
        if (
          acquired &&
          (phase === ScribeRecordingPhase.Idle ||
            phase === ScribeRecordingPhase.PermissionDenied)
        ) {
          await ScribeSession.releaseLease(context.authenticatedStaffer);
        }
        return;
      }
      if (!acquired) {
        this._snackBar.open(SCRIBE_LEASE_LOST_MESSAGE);
        await this._finaliseFailed(context);
        return;
      }
      this._consecutiveHeartbeatFailures = 0;
    } catch {
      if (this._context !== context || !this._isLeaseLive()) {
        return;
      }
      this._consecutiveHeartbeatFailures += 1;
      if (
        this._consecutiveHeartbeatFailures >=
        SCRIBE_MAX_CONSECUTIVE_HEARTBEAT_FAILURES
      ) {
        this._snackBar.open(SCRIBE_HEARTBEAT_FAILURE_MESSAGE);
        await this._finaliseFailed(context);
      }
    }
  }

  private _stopCaptureTimers(): void {
    if (this._elapsedTimer) {
      clearInterval(this._elapsedTimer);
      this._elapsedTimer = undefined;
    }
    if (this._bandLevelTimer) {
      clearInterval(this._bandLevelTimer);
      this._bandLevelTimer = undefined;
    }
  }

  private _stopLivenessTimers(): void {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = undefined;
    }
    if (this._tokenRefreshTimer) {
      clearTimeout(this._tokenRefreshTimer);
      this._tokenRefreshTimer = undefined;
    }
    if (this._tokenExpiryTimer) {
      clearTimeout(this._tokenExpiryTimer);
      this._tokenExpiryTimer = undefined;
    }
  }

  private _clearAllTimers(): void {
    this._stopCaptureTimers();
    this._stopLivenessTimers();
    if (this._writingTimeoutTimer) {
      clearTimeout(this._writingTimeoutTimer);
      this._writingTimeoutTimer = undefined;
    }
    if (this._writingElapsedTimer) {
      clearInterval(this._writingElapsedTimer);
      this._writingElapsedTimer = undefined;
    }
    this._clearSettledGraceTimer();
  }

  private _unsubscribeVox(): void {
    for (const unsubscribe of this._voxUnsubscribers) {
      unsubscribe();
    }
    this._voxUnsubscribers = [];
  }

  private _registerBeforeUnload(): void {
    if (this._beforeUnloadRegistered) {
      return;
    }
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
    this._beforeUnloadRegistered = true;
  }

  private _removeBeforeUnload(): void {
    if (!this._beforeUnloadRegistered) {
      return;
    }
    window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    this._beforeUnloadRegistered = false;
  }

  private _setLocalStatus(status: ScribeSessionStatus): void {
    this._activeSession.update((session) =>
      session ? { ...session, status } : undefined
    );
  }

  private _resetSessionState(): void {
    this._acceptedNoteKeys.clear();
    this._persistedNoteKeys.clear();
    this._bufferedNotes.clear();
    this._persistTasks = [];
    this._stoppedAtMs = undefined;
    this._postStopCompleted = false;
    this._latestBatchSettled = false;
    this._unavailableReason = undefined;
    this._recoveryRequested = false;
    this._noteRecoveryPending.set(false);
    this._sdkErrored = false;
    this._terminalReached = false;
    this._writingTimedOut = false;
    this._tokenExpired = false;
    this._finalising = false;
    this._writingResolve = undefined;
    this._awaitingNote.set(false);
    this._consecutiveHeartbeatFailures = 0;
    this._persistenceError.set(false);
    this._failureMessage.set(undefined);
    this._microphoneDenialReason.set(undefined);
    this._elapsed.set(0);
    this._writingElapsed.set(0);
    this._bandLevels.set(new Float32Array(0));
    this._boundPatient.set(undefined);
    this._launchUrl.set(undefined);
  }
}
